import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { fundingNotice, unknownFundingError, type FundingStatus } from './funding-state';
import { refreshFunding } from './refresh-funding';
import type { ДействиеОкна } from './deystvie-okna';
import { MIN_AMOUNT, parseMoney, warnManual } from './topup';
import {
  ВыборКошелька,
  ОТКУДА,
  первыйНепустой,
  useКошельки,
  type Source,
} from './koshelki';

/**
 * Положить денег на счёт кампании прямо сейчас — кнопка «+» в столбце
 * бюджета.
 *
 * ЭТО ЕДИНСТВЕННАЯ КНОПКА КАБИНЕТА, КОТОРАЯ ДВИГАЕТ ДЕНЬГИ ПО НАЖАТИЮ.
 * Всё прочее либо экономит (пауза, лимит), либо меняет то, что человек
 * уже разрешил тратить (ставки, минус-фразы). Отсюда устройство окна,
 * непохожее на остальные настройки:
 *
 *   — сумма проверяется ДО отправки, а не отказом Wildberries после;
 *   — источник виден и назван словами, а не выбран за человека;
 *   — на кнопке написано, что именно произойдёт, а не «Сохранить»;
 *   — исход «не знаем» показывается как «не знаем», а не как успех.
 *
 * ЗАЧЕМ ОНО ВООБЩЕ, ЕСЛИ ЕСТЬ АВТОПОПОЛНЕНИЕ. Автопополнение — правило
 * на будущее, и заводить его ради одного раза незачем: чтобы положить
 * тысячу сегодня, пришлось бы включить правило, дождаться его срабатывания
 * и не забыть выключить. Кнопка отвечает на другой вопрос — «нужно
 * сейчас».
 *
 * ПОЧЕМУ ЗДЕСЬ НЕТ СУТОЧНОГО ПОТОЛКА. Потолок ограничивает то, что сервис
 * тратит САМ, без нажатия. Запрещать человеку положить денег на его
 * собственную кампанию, потому что автоматика исчерпала свой потолок,
 * значит принять предохранитель за правило.
 */

/**
 * ═══════════════════════════════════════════════════════════════════════
 * КОШЕЛЬКИ ЖИВУТ В `koshelki.tsx` — ЗДЕСЬ ИХ БОЛЬШЕ НЕТ.
 *
 * Тот же выбор стоит в окне автопополнения, и пока разметок было две,
 * они разошлись: здесь карточки с суммами, там голые таблетки без
 * единого числа. Владелец 09.09.2026: «в автопополнении сделаем кнопки
 * с отображением как в пополнении ручным способом». Свести их —
 * значит держать выбор в одном месте, а не помнить про два.
 *
 * КОШЕЛЬКА «БОНУСЫ» БОЛЬШЕ НЕТ НИ В ОДНОМ ОКНЕ: решение владельца
 * того же дня, на замере (остатка бонусов не отдаёт ни один метод
 * рекламного API). Довод целиком — в шапке `koshelki.tsx`.
 * ═══════════════════════════════════════════════════════════════════════
 */

const money = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 });

interface Answer {
  ok: boolean;
  /** `yes` — деньги ушли; `unknown` — ответ Wildberries не понят. */
  applied: 'yes' | 'unknown';
  before: number | null;
  wbSourceType: number;
  message: string;
}

export function TopupNow({
  base,
  campaignId,
  title,
  balance,
  /** Сумма из настройки автопополнения — как разумное первое число. */
  suggested,
  onClose,
  onДействие,
  onDone,
}: {
  base: string;
  campaignId: string;
  title: string;
  balance: number | null;
  suggested?: number | null;
  onClose: () => void;
  /** Окно рисует «Положить …» в подвале — см. `deystvie-okna.ts`. */
  onДействие?: (д: ДействиеОкна | null) => void;
  onDone: (text: string) => void;
}) {
  /**
   * ПОЛЕ ЗАПОЛНЕНО, НО НЕ ПОДТВЕРЖДЕНО. Предложить сумму из настройки
   * автопополнения — помощь: чаще всего кладут именно столько. Подставить
   * её и сразу отправить было бы уже не помощью, а решением за человека
   * о его деньгах.
   */
  const [amount, setAmount] = useState(String(suggested && suggested >= MIN_AMOUNT ? suggested : MIN_AMOUNT));
  const [source, setSource] = useState<Source>('account');

  const qc = useQueryClient();
  const [uncertain, setUncertain] = useState<string|null>(null);
  const status = useQuery({queryKey:['topup',base,campaignId],
    queryFn:()=>api<FundingStatus>(`${base}/campaigns/${campaignId}/topup`),
    enabled:Boolean(campaignId),refetchInterval:q=>q.state.data?.unresolvedDeposit?5000:false});
  const blocked = uncertain !== null || Boolean(status.data?.unresolvedDeposit);

  const warn = warnManual(amount);
  const value = parseMoney(amount);

  const кошельки = useКошельки(base);

  /* ВЫБОР ПОДСТРАИВАЕТСЯ, КОГДА КОШЕЛЬКИ ДОЕХАЛИ, — и только если
     выбранный пуст. Двигать выбор под руками у человека, который уже
     нажал кнопку, нельзя: он смотрит не на неё, а на сумму. */
  const [самВыбрал, setСамВыбрал] = useState(false);
  useEffect(() => {
    if (самВыбрал || кошельки === null) return;
    /* НЕИЗВЕСТНЫЙ КОШЕЛЁК ВЫБОР НЕ ДВИГАЕТ: про него не сказано, что
       он пуст, — сказано, что мы не знаем. Уводить с него значит
       решать за Wildberries. */
    const выбранная = кошельки[source];
    if (выбранная === null || выбранная > 0) return;
    const есть = первыйНепустой(кошельки);
    if (есть !== null) setSource(есть);
  }, [кошельки, source, самВыбрал]);

  const send = useMutation({
    mutationFn: () =>
      api<Answer>(`${base}/campaigns/${campaignId}/topup/deposit`, {
        method: 'POST',
        body: { amount: value, source },
      }),
    onSuccess: async (r) => {
      onDone(r.message);
      // Остаток, история пополнений и сам список — всё это изменилось
      // одним действием, и обновиться должно тоже одним.
      if(r.applied==='unknown')setUncertain(r.message);
      await refreshFunding(qc,base);
      if(r.applied==='yes')onClose();
    },
    onError: (e) => {
      const message=e instanceof Error?e.message:'Не вышло';
      if(unknownFundingError(e))setUncertain('Результат запроса неизвестен. Проверьте историю пополнений перед повтором.');
      onDone(message);
    },
  });

  const from = ОТКУДА[source];

  /* ДЕЙСТВИЕ ОТДАЁТСЯ ОКНУ. Надпись своя — «Положить 1 000 ₽ с баланса»,
     а не «Сохранить»: нажатие тратит деньги, и слово должно стоить
     того же. Зависимости — только простые значения. */
  const занят = send.isPending;
  const нельзя = warn?.fatal === true || value === null || blocked || status.isPending || status.isFetching || status.isError;
  const надпись = `Положить ${value === null ? '' : `${money.format(value)} ₽ `}${from}`;
  useEffect(() => {
    if (onДействие === undefined) return;
    onДействие({
      запустить: () => send.mutate(),
      занят,
      нельзя,
      подсказка: blocked ? 'Повтор пополнения заблокирован до сверки.' : status.isError ? 'Не удалось проверить предыдущие пополнения.' : warn?.fatal === true ? warn.text : undefined,
      надпись,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [занят, нельзя, надпись]);

  return (
    <div className="flex flex-col gap-3">
      {(uncertain || fundingNotice(status.data)) && <p role="status" className="rk-inset">{uncertain || fundingNotice(status.data)}</p>}
      {status.isError && <p role="alert">Не удалось проверить предыдущие пополнения. <button className="rk-button-quiet" onClick={()=>void status.refetch()}>Повторить проверку</button></p>}

      <div className="flex flex-wrap items-baseline gap-2">
        <b>{title}</b>
        <span className="rk-quiet">
          {/* ОСТАТОК ПОКАЗЫВАЕТСЯ РЯДОМ С СУММОЙ, А НЕ ГДЕ-ТО ВЫШЕ.
              Решение «сколько положить» принимается сравнением с тем,
              сколько там сейчас; заставлять помнить это число
              при переходе — значит получать округлённые наугад суммы. */}
          {/* «НА СЧЁТЕ КАМПАНИИ», А НЕ «НА СЧЁТЕ». Слово из эталона
              (`на счёте кампании {{ modalBudget }}`), и оно нужно:
              в этом же окне рядом стоят ДРУГИЕ счета — счёт и баланс
              кабинета, — и «на счёте 579 ₽» читается как один из них. */}
          на счёте кампании {balance === null ? 'не узнан' : `${money.format(balance)} ₽`}
        </span>
      </div>

      <label className="flex flex-col gap-1">
        <span className="rk-quiet">Сколько положить, ₽</span>
        <input
          className="rk-field"
          inputMode="numeric"
          autoFocus
          disabled={занят || blocked}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        {/* ═══════════════════════════════════════════════════════════
            ПОРОГ WILDBERRIES СТОИТ ПОД ПОЛЕМ ВСЕГДА.

            Так в эталоне — подписью к полю, а не предупреждением. И это
            разные вещи: предупреждение появляется ПОСЛЕ того, как
            человек ввёл 500, и читается как отказ; подпись стоит
            до ввода и читается как правило.

            Число одно и то же и берётся из одного места (`MIN_AMOUNT`):
            рядом с ним живёт проверка, сверяющая его с исходником
            службы. Второе такое же число здесь, написанное словами,
            разошлось бы с первым молча — ровно так уже было, когда
            в кабинете стояла сотня, а на сервере тысяча.
            ═══════════════════════════════════════════════════════ */}
        <span data-d="порог-пополнения" className="rk-text-sm rk-faint">
          Меньше {money.format(MIN_AMOUNT)} ₽ Wildberries пополнить не даёт.
        </span>
      </label>

      <div className="k-группа-настройки">
        <span className="k-подпись-настройки">Откуда взять</span>
        {/* ═══════════════════════════════════════════════════════════
            КОШЕЛЬКИ — ОДНИ И ТЕ ЖЕ КАРТОЧКИ, ЧТО В АВТОПОПОЛНЕНИИ.

            У эталона (`data-d="кошельки-пополнения"`) каждый кошелёк —
            карточка `flex: 1 1 128px` с именем мелким и суммой крупнее
            имени. Так и выбирают: по сумме, а не по названию. Разметка
            переехала в `koshelki.tsx`, чтобы второе окно не рисовало
            то же самое иначе — а оно рисовало.
            ═══════════════════════════════════════════════════════ */}
        <ВыборКошелька
          кошельки={кошельки}
          выбран={source}
          onВыбор={(s) => {
            /* РУКА ЧЕЛОВЕКА СИЛЬНЕЕ ПОДСТРОЙКИ: после его нажатия
               выбор больше не двигается сам, даже если кошельки
               доедут позже и скажут, что там пусто. */
            setСамВыбрал(true);
            setSource(s);
          }}
        />
        {/* ЧЕГО МЫ НЕ ЗНАЕМ — ГОВОРИМ ВСЛУХ, НО КОРОТКО. Какой из
            источников скрывается за номером, который принимает
            Wildberries, их документация не раскрывает, а пробой это
            не берётся: различить источники можно лишь по тому, откуда
            ушли деньги. Молчание было бы обещанием точности, которой
            нет.

            А ПРЕЖНИЙ АБЗАЦ ЗАНИМАЛ В ОКНЕ СТОЛЬКО ЖЕ МЕСТА, СКОЛЬКО
            САМО ПОЛЕ, и читался как предупреждение об опасности. Это
            не опасность, а особенность чужого хозяйства, о которой
            достаточно сказать один раз — ровно так в эталоне. */}
        <span className="k-подпись-настройки" style={{ textWrap: 'pretty' }}>
          Wildberries не раскрывает, какой кошелёк стоит за каждым номером. Первое пополнение
          стоит сверить в их кабинете.
        </span>
      </div>

      {onДействие === undefined && (
        <div data-d="действия-ручного-пополнения" className="flex flex-wrap justify-end gap-2">
          <button className="rk-button-quiet" disabled={занят} onClick={onClose}>{blocked ? 'Закрыть' : 'Отмена'}</button>
          <button className="rk-button" disabled={нельзя || занят}
            title={warn?.fatal ? warn.text : undefined} onClick={() => send.mutate()}>
            {занят ? 'Пополняю…' : надпись}
          </button>
        </div>
      )}
      {warn && (
        <p
          className="rk-inset"
          style={{
            background: warn.fatal
              ? 'var(--bad-wash)'
              : 'var(--warn-wash)',
          }}
        >
          {warn.text}
        </p>
      )}


    </div>
  );
}
